import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import { colord } from "colord";

import type { StatusTransitionCode } from "components/status-transition-cell/status-transition-cell.component";
import TeamPill from "components/team-pill";
import {
  STAFF_TASK_COLORS_FEATURE,
  TEAMS_FEATURE
} from "constants/feature-flags";
import {
  TASK_STATUS_CANCELLED,
  TASK_STATUS_COMPLETED
} from "constants/staff-tasks";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import type { StaffTask } from "services/types/staff-tasks.type";
import { formatDateOnlyValue } from "utils/common";

import { AssignedTaskTags } from "../../../../components/assigned-tags";
import CommentsLink from "./comments-link-component";
import DueDateDisplay from "./due-date-display.component";
import FieldLabel from "./field-label.component";
import StaffTaskAssigneeDisplay from "./staff-task-assignee-display.component";
import StaffTaskDescription from "./staff-task-description.component";
import StaffTaskPatientDisplay from "./staff-task-patient-display.component";
import StaffTaskTitle from "./staff-task-title.component";
import TaskStatusField from "./task-status-field-component";
import { getCardBorderRadius } from "./utils";

type StaffTaskCardProps = {
  task: StaffTask;
  position?: "first" | "last" | "single";
  showPatient?: boolean;
  onUpdateCallback?: () => void;
};
export default function StaffTaskCard({
  task,
  position,
  showPatient = true,
  onUpdateCallback
}: StaffTaskCardProps) {
  const { handleStatusUpdate, handleDelete, handleOpenTaskModal } =
    useStaffTasksV2();
  const flags = useFlags();

  const isComplete =
    task.status === TASK_STATUS_COMPLETED ||
    task.status === TASK_STATUS_CANCELLED;

  const openTaskModalInViewMode = useCallback(() => {
    handleOpenTaskModal({ mode: "view", taskId: task.id });
  }, [handleOpenTaskModal, task.id]);

  const assignees = useMemo(
    () => [
      task.assignee,
      ...(task.assignees?.map((assignee) => assignee.user) || [])
    ],
    [task.assignee, task?.assignees]
  );

  const handleStatusChange = useCallback(
    (code: StatusTransitionCode) => handleStatusUpdate(task.id, code),
    [handleStatusUpdate, task.id]
  );

  return (
    <Stack
      width="100%"
      sx={{
        boxSizing: "border-box",
        borderRadius: getCardBorderRadius(position),
        borderTop: position === "single" ? `solid 1px ${grey[300]}` : undefined,
        borderBottom: `solid 1px ${isComplete ? grey[400] : grey[300]}`,
        background:
          !isComplete && flags[STAFF_TASK_COLORS_FEATURE] && task.color
            ? colord(task.color).alpha(0.15).toHex()
            : "white"
      }}
    >
      <Box
        width="100%"
        px={2}
        pt={1}
        pb={1.5}
        sx={{
          boxSizing: "border-box"
        }}
      >
        <Stack direction="row" columnGap={2} alignItems="center">
          <Stack direction="row" columnGap={2} alignItems="center" flex={1}>
            <FieldLabel label="Status" />
            <TaskStatusField
              value={task.status}
              onChange={handleStatusChange}
            />
          </Stack>

          <DueDateDisplay
            date={task.due_at}
            showDate={isComplete}
            isComplete={isComplete}
            sx={{ mr: -1 }}
          />
        </Stack>
        <StaffTaskTitle
          title={task.title}
          isComplete={isComplete}
          isCancelled={task.status === TASK_STATUS_CANCELLED}
          onClick={openTaskModalInViewMode}
        />
        {task.is_training && (
          <Chip
            label="Training"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: "12px", height: "20px", mt: 0.5 }}
          />
        )}

        {task.description && (
          <StaffTaskDescription
            description={task.description}
            onClick={openTaskModalInViewMode}
          />
        )}
      </Box>

      <Box
        width="100%"
        pr={2}
        pl={2}
        pt={1.5}
        pb={2}
        sx={{
          boxSizing: "border-box"
        }}
      >
        <AssignedTaskTags task={task} />
        <Stack direction="row" alignItems="flex-start">
          <Box
            sx={{
              display: "flex",
              flex: 1,
              cursor: "pointer"
            }}
          >
            <Stack rowGap={0.5}>
              <Stack direction="row" columnGap={2} alignItems="center" flex={1}>
                <FieldLabel label="Assignee" sx={{ width: "74px" }} />
                {flags[TEAMS_FEATURE] && task.team && (
                  <TeamPill name={task.team.name} color={task.team.color} />
                )}
                {flags[TEAMS_FEATURE] && task.team && !task.assignee?.id ? (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Unclaimed
                  </Typography>
                ) : (
                  <StaffTaskAssigneeDisplay
                    assignees={assignees}
                    isComplete={isComplete}
                  />
                )}
              </Stack>
              {showPatient && task.patient && task.patient.fullName && (
                <Stack
                  direction="row"
                  columnGap={2}
                  alignItems="center"
                  flex={1}
                  mb={0.25}
                >
                  <FieldLabel label="Patient" sx={{ width: "74px" }} />
                  <StaffTaskPatientDisplay
                    patient={task.patient}
                    isComplete={isComplete}
                  />
                </Stack>
              )}
              <Stack
                direction="row"
                columnGap={2}
                alignItems="flex-start"
                flex={1}
              >
                <FieldLabel label="Created" sx={{ width: "74px", mt: 0.28 }} />
                <Stack>
                  <Typography variant="body2">
                    by {task.created_by?.fullName}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: grey[600],
                      fontSize: "13px",
                      lineHeight: "18px"
                    }}
                  >
                    at {formatDateOnlyValue(task.created_at)}
                  </Typography>
                </Stack>
              </Stack>
              <Box width="100%" pl="90px">
                <CommentsLink
                  taskId={task.id}
                  count={task.comment_count ?? task.comments?.length ?? 0}
                  isComplete={isComplete}
                  onClick={openTaskModalInViewMode}
                />
              </Box>
            </Stack>
          </Box>

          <Stack
            sx={{
              width: "40px",
              mt: -1.5,
              mb: -2
            }}
            rowGap={1}
          >
            <IconButton
              sx={{ transform: "scale(0.95)", mb: -1 }}
              onClick={() => {
                handleOpenTaskModal({ mode: "edit", taskId: task.id });
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              sx={{ transform: "scale(0.95)", mb: -1 }}
              onClick={() => {
                handleDelete(task, onUpdateCallback);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
